import { useMemo, useState } from "react";
import { MiList, MiModalConfirm } from "@miplatform/components";
import {
  BUTTON_LABELS,
  CONFIG_PAGES,
  ENTITY_NAMES,
  MODAL_TITLES,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import { useDebounce, useRouter } from "@miplatform/hooks";
import {
  IInspectionTemplateListItemResponse,
  IVerticalTabProps,
} from "@miplatform/types";
import { AddButton, VerticalTabsLayout } from "@mibuildweb/components";
import { ConfigInspectionTemplateCard } from "./components/ConfigInspectionTemplateCard";
import {
  useInspectionTemplate,
  useInspectionTemplates,
  usePermissions,
} from "../../../hooks";
import { MODULES_CONFIG_PATHS } from "../../../routes/config/constants";
import { ConfigPageLayout } from "../ConfigPageLayout";

const ACTIVE = "Active";
const DRAFT = "Draft";

const tabs = [
  { label: "All", value: "All", filter: {} },
  {
    label: ACTIVE,
    value: ACTIVE,
    filter: {
      isActive: { eq: true },
    },
  },
  {
    label: DRAFT,
    value: DRAFT,
    filter: {
      isActive: { eq: false },
    },
  },
];

export function ConfigInspectionTemplateList() {
  const { navigate } = useRouter();
  const { hasPermission } = usePermissions();

  const [triggerRefresh, setTriggerRefresh] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedInspectionTemplate, setSelectedInspectionTemplate] =
    useState<IInspectionTemplateListItemResponse | null>(null);

  const {
    isLoading,
    getInspectionTemplates,
    inspectionTemplates,
    filter,
    setFilter,
    recentInspectionTemplates,
    getRecentInspectionTemplates,
  } = useInspectionTemplates();

  const { updateInspectionTemplate, deleteInspectionTemplate } =
    useInspectionTemplate();

  const canCreateInspectionTemplate = hasPermission(
    PERMISSIONS.INSPECTION_TEMPLATE.CREATE
  );

  const canDeleteInspectionTemplate = hasPermission(
    PERMISSIONS.INSPECTION_TEMPLATE.DELETE
  );
  const canUpdateInspectionTemplate = hasPermission(
    PERMISSIONS.INSPECTION_TEMPLATE.UPDATE
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleTabChange = (
    tab?: IVerticalTabProps<IInspectionTemplateListItemResponse[]> | null
  ) => {
    setFilter(tab?.filter ?? {});
  };

  const handleOpenDeleteModal = (item: IInspectionTemplateListItemResponse) => {
    setSelectedInspectionTemplate(item);
    setOpenDeleteModal(true);
  };

  const handleConfirmDeleteModal = async () => {
    if (!selectedInspectionTemplate) return;
    await deleteInspectionTemplate(selectedInspectionTemplate);
    setOpenDeleteModal(false);
    setTriggerRefresh((prev) => !prev);
  };

  const navigateToAddInspectionTemplate = () => {
    navigate(
      `${
        MODULES_CONFIG_PATHS[CONFIG_PAGES.MI_INSPECTIONS][
          CONFIG_PAGES.INSPECTION_TEMPLATES
        ]
      }/add-inspection-type`
    );
  };

  const handleUpdateInspectionTemplate = async (
    data: Partial<IInspectionTemplateListItemResponse>
  ) => {
    if (data.id) {
      await updateInspectionTemplate(data.id, data);
      setTriggerRefresh((prev) => !prev);
    }
  };

  const refreshData = useMemo(
    () => ({ triggerRefresh, filter }),
    [triggerRefresh, filter]
  );

  return (
    <ConfigPageLayout
      title={ENTITY_NAMES.INSPECTION_TEMPLATES}
      search={search}
      onHandleSearch={handleSearchChange}
      searchPlaceholder="Search"
      titleActionComponent={
        canCreateInspectionTemplate && (
          <AddButton
            label={`Add ${ENTITY_NAMES.INSPECTION_TEMPLATE}`}
            onClick={navigateToAddInspectionTemplate}
          />
        )
      }
      loading={isLoading}
      hideOnBack
    >
      <VerticalTabsLayout
        hideStatusIcons
        tabs={tabs}
        onTabChange={handleTabChange}
        hideNavigationButtons
      >
        <MiList
          viewType="card"
          data={inspectionTemplates ?? []}
          dataProvider={getInspectionTemplates}
          loading={isLoading}
          CardComponent={ConfigInspectionTemplateCard}
          cardProps={{
            handleOpenDeleteModal,
            updateInspectionTemplate: handleUpdateInspectionTemplate,
            canUpdateInspectionTemplate,
            canDeleteInspectionTemplate,
          }}
          paginationMode={PAGINATION.SERVER}
          search={debouncedSearch}
          recentData={recentInspectionTemplates}
          recentDataProvider={getRecentInspectionTemplates}
          entityName={ENTITY_NAMES.INSPECTION_TEMPLATES}
          refresh={refreshData}
          emptyListProps={{
            text: `No ${ENTITY_NAMES.INSPECTION_TEMPLATES} found`,
          }}
        />
      </VerticalTabsLayout>
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        open={openDeleteModal}
        textPositive={BUTTON_LABELS.DELETE}
        handleNegative={() => setOpenDeleteModal(false)}
        handlePositive={handleConfirmDeleteModal}
        description={`You are about to delete ${selectedInspectionTemplate?.name}. Are you sure you would like to continue?`}
      />
    </ConfigPageLayout>
  );
}
